import { useState, useCallback, useEffect } from 'react';
import { SocialMediaAccountPublic, SocialPlatform } from '@/lib/api';
import { socialMediaApi } from '../api';
import { toast } from '@/components/ui/use-toast';

/**
 * Lists + connects + disconnects social-media accounts for a Holder.
 * The caller passes the Holder ID (resolved from `useHolderSwitcher`);
 * when no Holder is selected the hook stays in a loading state and
 * does not fire any requests.
 */
export function useSocialAccounts(holderId: string | null) {
  const [accounts, setAccounts] = useState<SocialMediaAccountPublic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    if (!holderId) {
      setAccounts([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await socialMediaApi.listSocialMediaAccounts(holderId);
      setAccounts(result.accounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      setIsLoading(false);
    }
  }, [holderId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const connectAccount = useCallback(
    async (platform: SocialPlatform) => {
      if (!holderId) {
        toast({
          title: 'Pick a workspace',
          description: 'Select a workspace before connecting an account.',
          variant: 'destructive',
        });
        return;
      }
      try {
        const redirectUri = `${window.location.origin}/v1/arches/social-media/accounts/oauth/callback`;
        const result = await socialMediaApi.initSocialMediaOAuth(holderId, platform, redirectUri);
        window.location.href = result.authorizationUrl;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start account connection';
        toast({ title: 'Connection failed', description: message, variant: 'destructive' });
      }
    },
    [holderId],
  );

  const disconnectAccount = useCallback(
    async (accountId: string) => {
      if (!holderId) return;
      try {
        await socialMediaApi.disconnectSocialMediaAccount(holderId, accountId);
        setAccounts((prev) => prev.filter((a) => a.accountId !== accountId));
        toast({ title: 'Account disconnected' });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to disconnect account';
        toast({ title: 'Error', description: message, variant: 'destructive' });
      }
    },
    [holderId],
  );

  return { accounts, isLoading, error, loadAccounts, connectAccount, disconnectAccount };
}
